/**
 * Custom pump.fun-style connect modal.
 *
 * This is OUR UI — not the wallet's approval popup (which a dapp can never
 * restyle). It mimics the pump.fun connect card (branded header + an "ALLOW
 * THIS SITE TO" permission list + an Auto-approve control) and does it one
 * better: our auto-approve is the existing session-key "rapid-fire" — gas-
 * capped, on-chain enforced, revocable, and wallet-agnostic.
 *
 * Connect flow: pick a wallet → `select(name)` → (effect) `connect()` once the
 * selection lands → the wallet's own popup appears, now branded by the
 * dapp-metadata fix in main.tsx. Arming auto-approve happens in ConnectButton
 * AFTER connect (a fresh `useWallet()` snapshot with a live publicKey) to avoid
 * a stale-closure on the just-connected key.
 */
import { WalletReadyState } from "@solana/wallet-adapter-base";
import { useWallet, type Wallet } from "@solana/wallet-adapter-react";
import React, { useEffect, useState } from "react";

import type { Brand } from "../launch/brand";
import { useStore } from "../store";

interface ConnectModalProps {
  brand: Brand;
  onClose: () => void;
  autoApprove: boolean;
  onAutoApproveChange: (v: boolean) => void;
  /** Called the moment a connect is initiated, so the parent can arm after. */
  onConnectInitiated: (autoApprove: boolean) => void;
}

export function ConnectModal({
  brand,
  onClose,
  autoApprove,
  onAutoApproveChange,
  onConnectInitiated,
}: ConnectModalProps): JSX.Element {
  const { wallets, select, connect, connecting, connected, wallet } = useWallet();
  const [pendingName, setPendingName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const a = brand.accent;
  const broadcastPubkey = useStore((s) => s.broadcastPubkey);
  const setBroadcastPubkey = useStore((s) => s.setBroadcastPubkey);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Once `select(name)` has propagated (wallet matches) and we're not already
  // connecting, fire the actual connect — autoConnect won't pop a popup for a
  // first-time site, so we must connect explicitly.
  useEffect(() => {
    if (!pendingName) return;
    if (!wallet || wallet.adapter.name !== pendingName) return;
    if (connecting || connected) return;
    let cancelled = false;
    void (async () => {
      try {
        onConnectInitiated(autoApprove);
        await connect();
        // Connected — the parent's effect handles arming + closing.
      } catch (e) {
        if (!cancelled) {
          setError(friendlyConnectError(e));
          setPendingName(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // autoApprove intentionally read at click time via onConnectInitiated.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingName, wallet, connecting, connected, connect]);

  const onPick = (w: Wallet): void => {
    setError(null);
    if (w.readyState === WalletReadyState.NotDetected) {
      if (w.adapter.url) window.open(w.adapter.url, "_blank", "noopener");
      return;
    }
    select(w.adapter.name);
    setPendingName(w.adapter.name);
  };

  // Installed/Loadable wallets first; hide Unsupported entirely; surface
  // NotDetected last as "install" links so the list is never empty-feeling.
  const ranked = [...wallets]
    .filter((w) => w.readyState !== WalletReadyState.Unsupported)
    .sort((x, y) => rank(x.readyState) - rank(y.readyState));

  const busy = connecting || pendingName !== null;

  return (
    <div
      style={overlayStyle}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Connect to ${brand.appName}`}
    >
      <div style={cardStyle(a)} onClick={(e) => e.stopPropagation()}>
        <header style={headerStyle}>
          <img src={brand.iconUrl} alt="" width={40} height={40} style={{ borderRadius: 10 }} />
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 17, fontWeight: 700, color: a.text }}>
              {brand.appName}
            </span>
            <span style={{ fontSize: 12.5, color: a.subtext }}>{brand.tagline}</span>
          </div>
        </header>

        <div style={{ ...labelStyle, color: a.subtext }}>ALLOW THIS SITE TO:</div>
        <ul style={permListStyle}>
          <CheckRow accent={a} checked={broadcastPubkey} onChange={setBroadcastPubkey}>
            Show your wallet address to other players
          </CheckRow>
          <AutoApproveRow accent={a} checked={autoApprove} onChange={onAutoApproveChange} />
        </ul>

        <div style={{ ...labelStyle, color: a.subtext, marginTop: 14 }}>CHOOSE A WALLET</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {ranked.length === 0 && (
            <div style={{ fontSize: 13, color: a.subtext, padding: "8px 2px" }}>
              No Solana wallet detected. Install{" "}
              <a href="https://phantom.app/" target="_blank" rel="noopener" style={{ color: a.primary }}>
                Phantom
              </a>{" "}
              or{" "}
              <a href="https://solflare.com/" target="_blank" rel="noopener" style={{ color: a.primary }}>
                Solflare
              </a>{" "}
              and reload.
            </div>
          )}
          {ranked.map((w) => {
            const detected =
              w.readyState === WalletReadyState.Installed ||
              w.readyState === WalletReadyState.Loadable;
            const isPending = pendingName === w.adapter.name;
            return (
              <button
                key={w.adapter.name}
                onClick={() => onPick(w)}
                disabled={busy && !isPending}
                style={walletRowStyle(a, isPending)}
              >
                <img src={w.adapter.icon} alt="" width={22} height={22} />
                <span style={{ flex: 1, textAlign: "left", fontWeight: 600 }}>
                  {w.adapter.name}
                </span>
                <span style={{ fontSize: 11, color: detected ? a.primary : a.subtext }}>
                  {isPending ? "Connecting…" : detected ? "Detected" : "Install"}
                </span>
              </button>
            );
          })}
        </div>

        {error && (
          <div style={{ color: "#f4a4b4", fontSize: 12.5, marginTop: 10 }}>{error}</div>
        )}

        <footer style={{ marginTop: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 11, color: a.subtext }}>Only connect to apps you trust</span>
          <button onClick={onClose} style={cancelBtnStyle(a)}>
            Cancel
          </button>
        </footer>
      </div>
    </div>
  );
}

function CheckRow({
  accent,
  checked,
  onChange,
  children,
}: {
  accent: Brand["accent"];
  checked: boolean;
  onChange: (v: boolean) => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <li style={permRowStyle}>
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          width: "100%",
          padding: 0,
          background: "transparent",
          border: "none",
          cursor: "pointer",
          color: "inherit",
          font: "inherit",
          textAlign: "left",
        }}
      >
        <CheckBox color={accent.primary} checked={checked} />
        <span style={{ color: checked ? accent.text : accent.subtext }}>{children}</span>
      </button>
    </li>
  );
}

function AutoApproveRow({
  accent,
  checked,
  onChange,
}: {
  accent: Brand["accent"];
  checked: boolean;
  onChange: (v: boolean) => void;
}): JSX.Element {
  return (
    <li style={{ ...permRowStyle, alignItems: "flex-start" }}>
      <span style={{ fontSize: 15, lineHeight: "20px" }}>⚡</span>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 3 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <span style={{ color: accent.text }}>Auto-approve touches for 15 min</span>
          <Toggle accent={accent} checked={checked} onChange={onChange} />
        </div>
        <span style={{ fontSize: 11, color: accent.subtext, lineHeight: 1.4 }}>
          Gas-capped (~0.03 SOL · ~20 touches) · on-chain enforced · revoke anytime · works with any wallet
        </span>
      </div>
    </li>
  );
}

function Toggle({
  accent,
  checked,
  onChange,
}: {
  accent: Brand["accent"];
  checked: boolean;
  onChange: (v: boolean) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      style={{
        width: 40,
        height: 22,
        borderRadius: 999,
        border: "none",
        cursor: "pointer",
        background: checked ? accent.primary : "#3a3a48",
        position: "relative",
        transition: "background 120ms",
        flexShrink: 0,
        padding: 0,
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 2,
          left: checked ? 20 : 2,
          width: 18,
          height: 18,
          borderRadius: "50%",
          background: "#fff",
          transition: "left 120ms",
        }}
      />
    </button>
  );
}

function CheckBox({ color, checked }: { color: string; checked: boolean }): JSX.Element {
  return (
    <svg width={16} height={16} viewBox="0 0 16 16" style={{ flexShrink: 0 }} aria-hidden>
      {checked ? (
        <>
          <rect x="0.5" y="0.5" width="15" height="15" rx="4" fill={color} opacity="0.18" />
          <path d="M4 8.2 L7 11 L12 5" stroke={color} strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </>
      ) : (
        <rect x="1" y="1" width="14" height="14" rx="4" fill="none" stroke={color} strokeOpacity="0.45" strokeWidth="1.3" />
      )}
    </svg>
  );
}

/** Installed/Loadable rank ahead of NotDetected for list ordering. */
function rank(rs: WalletReadyState): number {
  if (rs === WalletReadyState.Installed) return 0;
  if (rs === WalletReadyState.Loadable) return 1;
  return 2;
}

export function friendlyConnectError(e: unknown): string {
  const msg = e instanceof Error ? e.message : String(e);
  if (/user rejected|request rejected|reject/i.test(msg)) return "Connection request rejected.";
  if (/not (installed|detected|found)|WalletNotReady/i.test(msg)) {
    return "Wallet not detected — install it and reload.";
  }
  return msg.split("\n")[0]?.slice(0, 140) || "Failed to connect.";
}

// --- styles -----------------------------------------------------------------

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(4,4,8,0.66)",
  backdropFilter: "blur(2px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
  pointerEvents: "auto",
  padding: 16,
};

function cardStyle(a: Brand["accent"]): React.CSSProperties {
  return {
    width: 372,
    maxWidth: "100%",
    maxHeight: "90vh",
    overflowY: "auto",
    background: a.card,
    border: `1px solid ${a.border}`,
    borderRadius: 16,
    padding: 20,
    boxShadow: "0 24px 60px rgba(0,0,0,0.55)",
    color: a.text,
    fontFamily: "ui-sans-serif, system-ui, sans-serif",
  };
}

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  marginBottom: 18,
};

const labelStyle: React.CSSProperties = {
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: 0.8,
  marginBottom: 8,
};

const permListStyle: React.CSSProperties = {
  listStyle: "none",
  margin: 0,
  padding: 0,
  display: "flex",
  flexDirection: "column",
  gap: 10,
};

const permRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  fontSize: 13.5,
};

function walletRowStyle(a: Brand["accent"], pending: boolean): React.CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    padding: "10px 12px",
    background: pending ? "rgba(63,208,122,0.10)" : "rgba(255,255,255,0.03)",
    border: `1px solid ${pending ? a.primary : a.border}`,
    borderRadius: 10,
    color: a.text,
    fontSize: 14,
    cursor: "pointer",
  };
}

function cancelBtnStyle(a: Brand["accent"]): React.CSSProperties {
  return {
    padding: "8px 16px",
    background: "transparent",
    color: a.subtext,
    border: `1px solid ${a.border}`,
    borderRadius: 999,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  };
}
